import React, { useState } from "react";
import { Loader2, Lock, Mail, User } from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AuthDialog({ open, onOpenChange, initialMode = "signup" }) {
  const { login, refresh } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: "", password: "", full_name: "", phone: "", country: "",
  });

  React.useEffect(() => { setMode(initialMode); }, [initialMode, open]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "signup") {
        const { data } = await api.post("/auth/signup", form);
        if (data.token) localStorage.setItem("tti_token", data.token);
        await refresh();
        toast.success(`Welcome ${form.full_name.split(" ")[0]}!`);
        onOpenChange(false);
      } else {
        const r = await login(form.email, form.password);
        if (r.ok) {
          toast.success("Welcome back!");
          onOpenChange(false);
        } else {
          toast.error(r.error);
        }
      }
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-cream border-cream-200" data-testid="auth-dialog">
        <DialogHeader>
          <div className="eyebrow mb-2">{mode === "signup" ? "Create account" : "Welcome back"}</div>
          <DialogTitle className="font-serif text-3xl tracking-tight text-deepred">
            {mode === "signup" ? "Join TTI Holidays" : "Sign in"}
          </DialogTitle>
          <DialogDescription>
            {mode === "signup" ? "Save tours, manage bookings, download itineraries." : "Continue your travel planning."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2" data-testid="auth-form">
          {mode === "signup" && (
            <div>
              <Label>Full name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input data-testid="auth-name" required value={form.full_name} onChange={update("full_name")} placeholder="Jane Doe" className="pl-10 bg-white h-11" />
              </div>
            </div>
          )}
          <div>
            <Label>Email *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="auth-email" type="email" required value={form.email} onChange={update("email")} placeholder="you@example.com" className="pl-10 bg-white h-11" />
            </div>
          </div>
          <div>
            <Label>Password *</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="auth-password" type="password" required value={form.password} onChange={update("password")} placeholder={mode === "signup" ? "Min 8 characters" : "••••••••"} minLength={mode === "signup" ? 8 : undefined} className="pl-10 bg-white h-11" />
            </div>
          </div>
          {mode === "signup" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Phone</Label>
                <Input data-testid="auth-phone" value={form.phone} onChange={update("phone")} placeholder="+44…" className="bg-white h-11" />
              </div>
              <div>
                <Label>Country</Label>
                <Input data-testid="auth-country" value={form.country} onChange={update("country")} placeholder="United Kingdom" className="bg-white h-11" />
              </div>
            </div>
          )}

          <button type="submit" disabled={loading} data-testid="auth-submit" className="btn-primary w-full h-12 disabled:opacity-60">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {mode === "signup" ? "Creating…" : "Signing in…"}</> : (mode === "signup" ? "Create my account" : "Sign in")}
          </button>

          <div className="text-center text-sm text-foreground/65">
            {mode === "signup" ? "Already have an account? " : "New traveller? "}
            <button type="button" onClick={() => setMode(mode === "signup" ? "login" : "signup")} className="text-saffron font-medium hover:underline" data-testid="auth-switch">
              {mode === "signup" ? "Sign in" : "Create one"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
